import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { SignedInResultModel } from './login/model/signed-in-result.model';
import { SignInModel } from './login/model/sign-in.model';
import { RoleModel } from './management/role/model/role.model';
import { UserCreationIamModel } from './management/users/user-create/model/user-creation-iam.model';
import { UserTimelineEntity } from './management/users/timeline/model/user-timeline.model';
import { PaginatedModel } from '../../shared/component/select/pagination/model/paginated.model';
import { UserUpdateModel } from './management/users/model/user-update.model';
import { UserModel } from './user-settings/model/user.model';
import { UserStatusEnum } from './user-settings/enum/user-status.enum';
import { UserSessionModel } from './login/model/user-session.model';

@Injectable({
    providedIn: 'root'
})
export class IamService {
    BASE = environment.IAM;
    HEALTH_ANALYSIS = environment.HEALTH_ANALYSIS;

    constructor(protected httpService: HttpClient) { }

    private buildQueryParams(searchTerm: string | undefined, limit: number = 10, page: number = 1) {
        let queryParams = '';
        if (searchTerm) {
            queryParams = `?searchTerm=${searchTerm}`;
        }
        queryParams += `${queryParams ? '&' : '?'}limit=${limit}&page=${page}`;
        return queryParams;
    }
    public createUser(user: UserCreationIamModel): Observable<UserModel> {
        return this.httpService.post<UserModel>(`${this.BASE}/iam/user`, user);
    }
    public deleteUser(userId: string): Observable<void> {
        return this.httpService.delete<void>(`${this.BASE}/iam/user/${userId}`);
    }
    public getUser(userId: string): Observable<UserModel> {
        return this.httpService.get<UserModel>(`${this.BASE}/iam/user/${userId}`);
    }
    public getUsers(searchTerm?: string, page = 1, limit = 10): Observable<PaginatedModel<UserModel>> {
        const queryParams = this.buildQueryParams(searchTerm, limit, page);
        return this.httpService.get<PaginatedModel<UserModel>>(`${this.BASE}/iam/user${queryParams}`);
    }
    public getUserTimeline(userId: string): Observable<Array<UserTimelineEntity>> {
        return this.httpService.get<Array<UserTimelineEntity>>(`${this.BASE}/iam/user/${userId}/timeline`);
    }
    public getRoles(): Observable<Array<RoleModel>> {
        return this.httpService.get<Array<RoleModel>>(`${this.BASE}/iam/role`);
    }
    public signIn(signForm: SignInModel): Observable<SignedInResultModel> {
        return this.httpService.post<SignedInResultModel>(`${this.BASE}/sign/in`, signForm, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
    }
    public signInWithOtp(signForm: SignInModel): Observable<SignedInResultModel> {
        return this.httpService.post<SignedInResultModel>(`${this.BASE}/sign/in/otp`, signForm, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
    }
    public passwordReset(userId: string): Observable<void> {
        return this.httpService.patch<void>(`${this.BASE}/iam/user/${userId}/password/reset`, {});
    }
    public requestPasswordChange(email: string): Observable<void> {
        return this.httpService.patch<void>(`${this.BASE}/user/password/change/request`, { email });
    }
    public signOut(sessionId: string): Observable<void> {
        return this.httpService.delete<void>(`${this.BASE}/sign/out/${sessionId}`);
    }
    public refreshSession(sessionId: string): Observable<SignedInResultModel> {
        return this.httpService.patch<SignedInResultModel>(`${this.BASE}/session/${sessionId}/refresh`, {});
    }
    public updateUser(userId: string, user: UserUpdateModel): Observable<UserModel> {
        return this.httpService.patch<UserModel>(`${this.BASE}/iam/user/${userId}`, user);
    }
    public updateUserStatus(userId: string, status: UserStatusEnum): Observable<void> {
        return this.httpService.patch<void>(`${this.BASE}/iam/user/${userId}/status`, { status });
    }
    public getUserSessions(): Observable<UserSessionModel[]> {
        return this.httpService.get<UserSessionModel[]>(`${this.BASE}/session/user`);
    }
    public deleteSession(sessionId: string): Observable<void> {
        return this.httpService.delete<void>(`${this.BASE}/session/${sessionId}`);
    }
}
